import { GridColDef } from '@mui/x-data-grid-premium';
import React, { useMemo } from 'react';
import { PricePerUnit } from '@retail/products/components';
import { MemberMgSupplierCampaignItem } from '@retail/campaign/types';
import { useFormContext } from 'react-hook-form';
import { AddCampaignItemsSchema, MemberCampaignItemSchema } from './addItemsSchema';
import { ToggleButtonGroup, Tooltip } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { dataGridBoldClass } from '@shared/styles';
import { PercentageInput } from './PercentageInput';
import { ToggleButton } from '@shared/components';
import { PriceInput } from './PriceInput';
import { StackedTextCell } from '@retail/components';

const baseColumnProps: Partial<GridColDef> = {
  width: 160,
  align: 'left',
  headerAlign: 'left',
  headerName: '',
  editable: false,
  disableColumnMenu: true,
  sortable: false,
};

export const useLocalPricesColumns = () => {
  const t = useAppTFunction();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddCampaignItemsSchema<MemberCampaignItemSchema>>();

  return useMemo(() => {
    const getLocalPurchasePriceColumn = (): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      field: 'localPurchasePrice',
      headerName: t('campaign.columns.localPurchasePrice'),
      renderCell: ({ row }) => {
        const primaryUnit = row.getPrimaryPackageUnit();

        const purchaseUnitPrice = row.prices?.purchasePriceExVat;

        return purchaseUnitPrice ? (
          <StackedTextCell
            gap="small"
            topLine={<PricePerUnit price={purchaseUnitPrice} unit={primaryUnit?.unit} />}
            bottomLine={t('campaign.columns.exVat')}
          />
        ) : (
          '-'
        );
      },
    });

    const getCampaignSalesPriceColumn = (): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 140,
      headerAlign: 'center',
      align: 'center',
      field: 'campaignSalesPrice',
      headerName: t('campaign.columns.campaignSalesPrice.label'),
      renderCell: ({ row }) => {
        const error = errors.items?.[row.mgSupplierItemId]?.campaignSalesPrice;
        const useDiscount = watch(`items.${row.mgSupplierItemId}.useDiscount`);
        return (
          <PriceInput
            error={error?.message}
            fieldName={`items.${row.mgSupplierItemId}.campaignSalesPrice`}
            control={control}
            incVat={true}
            disabled={!!useDiscount}
          />
        );
      },
    });

    const getLocalSalesPriceColumn = (
      includeVat: boolean
    ): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      field: 'localSalesPrice',
      headerName: t('campaign.columns.localSalesPrice'),
      renderCell: ({ row }) => {
        const campaignUnit = watch('items')[row.mgSupplierItemId]?.campaignUnit;
        const primaryPackageUnit = row.getPrimaryPackageUnit() || row.packages[0].unit;
        const givenUnit = campaignUnit ?? primaryPackageUnit.unit;

        const selectedPackage = row.packages.find((pgk) => pgk.unit.unit === givenUnit);
        const prices = row.prices;

        const packagePrice =
          selectedPackage?.packageType === 'PRIMARY'
            ? includeVat
              ? prices?.primaryIncVat
              : prices?.primaryExVat
            : selectedPackage?.packageType === 'SECONDARY'
            ? includeVat
              ? prices?.secondaryIncVat
              : prices?.secondaryExVat
            : selectedPackage?.packageType === 'TERTIARY'
            ? includeVat
              ? prices?.tertiaryIncVat
              : prices?.tertiaryExVat
            : '';
        return packagePrice ? (
          <StackedTextCell
            gap="small"
            topLine={<PricePerUnit price={packagePrice} unit={givenUnit} />}
            bottomLine={includeVat ? t('campaign.columns.incVat') : t('campaign.columns.exVat')}
          />
        ) : (
          '-'
        );
      },
    });

    const getLocalSalesCoverageColumn = (): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      cellClassName: () => dataGridBoldClass,
      field: 'localSalesCoverage',
      headerName: t('campaign.columns.localSalesCoverage'),
      renderCell: ({ row }) => row.getFormattedSalesCoverage(),
    });

    const getPrimaryDiscountPercentageColumn = (): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 140,
      headerAlign: 'center',
      align: 'center',
      field: 'discountPercentage',
      headerName: t('campaign.columns.discountPercentage'),
      renderCell: ({ row }) => {
        const error = errors.items?.[row.mgSupplierItemId]?.primaryDiscountPercentage;

        const useDiscount = watch(`items.${row.mgSupplierItemId}.useDiscount`);
        return (
          <PercentageInput
            error={error?.message}
            disabled={!useDiscount}
            fieldName={`items.${row.mgSupplierItemId}.primaryDiscountPercentage`}
            control={control}
          />
        );
      },
    });

    const getUseDiscountOrPriceColumn = (): GridColDef<MemberMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 140,
      headerAlign: 'center',
      align: 'center',
      field: 'discountOrPrice',
      headerName: t('campaign.columns.discountOrPrice'),
      renderCell: ({ row }) => {
        const useDiscount = watch(`items.${row.mgSupplierItemId}.useDiscount`);

        const currentSalesPrice = row.prices?.primaryExVat;

        return (
          <Tooltip title={t('campaign.messages.needSalesPriceForDiscount')}>
            {!currentSalesPrice ? (
              <ToggleButtonGroup
                sx={{
                  border: (theme) => `1px solid${theme.vars.palette.primary.main}`,
                  p: 0.4,
                  height: 32,
                }}
                value={'price'}
                disabled
              >
                <ToggleButton fullWidth value="price" size="small">
                  KR
                </ToggleButton>
                <ToggleButton fullWidth value="discount" size="small">
                  %
                </ToggleButton>
              </ToggleButtonGroup>
            ) : (
              <ToggleButtonGroup
                sx={{
                  border: (theme) => `1px solid${theme.vars.palette.primary.main}`,
                  p: 0.4,
                  height: 32,
                }}
                value={!currentSalesPrice ? 'price' : useDiscount ? 'discount' : 'price'}
                disabled={!currentSalesPrice}
              >
                <ToggleButton
                  fullWidth
                  value="price"
                  size="small"
                  onClick={() => setValue(`items.${row.mgSupplierItemId}.useDiscount`, false)}
                >
                  KR
                </ToggleButton>
                <ToggleButton
                  fullWidth
                  value="discount"
                  size="small"
                  onClick={() => setValue(`items.${row.mgSupplierItemId}.useDiscount`, true)}
                >
                  %
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Tooltip>
        );
      },
    });

    return {
      getLocalPurchasePriceColumn,
      getLocalSalesPriceColumn,
      getLocalSalesCoverageColumn,
      getPrimaryDiscountPercentageColumn,
      getUseDiscountOrPriceColumn,
      getCampaignSalesPriceColumn,
    };
  }, [control, errors.items, setValue, t, watch]);
};
